import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { FilterProfile, FilterProfileInput } from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';

interface FilterProfilesResponse {
  profiles: FilterProfile[];
}

interface FilterProfileResponse {
  profile: FilterProfile;
}

export const jobFilterProfileKeys = {
  all: ['jobFilterProfiles'] as const,
  lists: () => [...jobFilterProfileKeys.all, 'list'] as const,
  list: () => jobFilterProfileKeys.lists(),
};

export function useJobFilterProfiles() {
  return useQuery({
    queryKey: jobFilterProfileKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<FilterProfilesResponse>('/job-filter-profiles');
      return response.profiles;
    },
  });
}

export function useSaveJobFilterProfile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: FilterProfileInput) => {
      const response = await apiClient.post<FilterProfileResponse>('/job-filter-profiles', data);
      return response.profile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobFilterProfileKeys.list() });
      toast({
        title: 'Profile saved',
        description: 'Your filter profile was saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save profile.',
        variant: 'destructive',
      });
    },
  });
}

export function useSetDefaultJobFilterProfile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.patch<FilterProfileResponse>(
        `/job-filter-profiles/${id}/default`
      );
      return response.profile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobFilterProfileKeys.list() });
      toast({
        title: 'Default updated',
        description: 'Default profile updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to set default profile.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteJobFilterProfile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/job-filter-profiles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobFilterProfileKeys.list() });
      toast({
        title: 'Profile deleted',
        description: 'The profile has been removed.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete profile.',
        variant: 'destructive',
      });
    },
  });
}
